import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import BizBirdCard from "@/components/BizBirdCard";
import LemonBotJr from "@/components/LemonBotJr";
import MissionVideo from "@/components/MissionVideo";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_BIRDS,
  JR_BIRD_CONFIRMATIONS,
} from "@/utils/jrDemoState";
import { getMissionVideo } from "@/utils/jrMissionMedia";
import { Sparkles } from "lucide-react";

const BIRD_TAGLINES = {
  eagle: "I like to lead and make decisions.",
  parrot: "I like to talk, share, and present.",
  dove: "I like to help and encourage my team.",
  owl: "I like to plan and check details.",
};

export default function Mission2() {
  const navigate = useNavigate();
  const [bird, setBird] = useState(loadJrState().bizBird || null);
  const [showOverlay, setShowOverlay] = useState(false);
  const video = getMissionVideo(2);

  const onContinue = () => {
    if (!bird) return;
    saveJrState({ bizBird: bird });
    markMissionComplete(2, { customized: true });
    setShowOverlay(true);
  };

  return (
    <ElementaryMissionPage
      missionNumber={2}
      title="Meet the Biz Birds"
      lesson="Every team needs different strengths. Pick the bird that sounds most like you."
      canContinue={!!bird}
      onContinue={onContinue}
      testId="elementary-mission-2-page"
    >
      {video && (
        <MissionVideo url={video.url} title={video.title} testId="mission-2-video" />
      )}
      <div className="mb-6">
        <LemonBotJr
          tone="green"
          greeting="Every leader has strengths. Which bird sounds most like you?"
          prompts={[
            "There's no wrong answer.",
            "Pick the one that feels most like YOU.",
          ]}
          compact
        />
      </div>

      <p className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
        Which bird sounds most like you?
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(JR_BIRDS).map(([key, b]) => {
          const selected = bird === key;
          return (
            <div key={key} className="flex flex-col">
              <BizBirdCard
                emoji={b.emoji}
                image={b.image}
                name={b.name}
                role={b.role}
                description={BIRD_TAGLINES[key] || b.description}
                selected={selected}
                onSelect={() => setBird(key)}
                testId={`mission-2-bird-${key}`}
              />
              <button
                type="button"
                onClick={() => setBird(key)}
                className={`mt-2 w-full rounded-full font-bold text-sm py-2.5 transition-all ${
                  selected
                    ? "bg-yellow-500 text-white shadow"
                    : "bg-white text-yellow-700 ring-2 ring-yellow-200 hover:ring-yellow-400 hover:bg-yellow-50"
                }`}
                data-testid={`mission-2-bird-${key}-pick`}
              >
                {selected ? "Picked!" : "This sounds like me"}
              </button>
            </div>
          );
        })}
      </div>

      {bird && (
        <div
          className="mt-6 bg-gradient-to-r from-yellow-50 to-amber-50 ring-2 ring-yellow-300 rounded-2xl p-4 flex items-start gap-3"
          data-testid="mission-2-confirmation"
        >
          <div className="bg-yellow-500 text-white w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1">
              Your strength
            </p>
            <p className="text-gray-900 text-base font-semibold leading-snug">
              {JR_BIRD_CONFIRMATIONS[bird]}
            </p>
          </div>
        </div>
      )}

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={2}
          missionTitle="Meet the Biz Birds"
          message="You found your strength! Time to find a problem your bird can help solve."
          continueLabel="On to Mission 3"
          onContinue={() => navigate("/elementary/demo/mission/3")}
        />
      )}
    </ElementaryMissionPage>
  );
}
